import styled from "@emotion/styled";
import { Row } from "@tanstack/react-table";
import { SkeletonBlock, SkeletonCircle } from "components/general/Skeleton";
import { Direction, TokenStackedArray } from "components/general/TokenStackedArray";
import { flexBetween } from "config/commonCSS";
import { useIsTradeHistoryReturned } from "hooks/useIsTradeHistoryReturned";
import { GlobalStore } from "store/global/GlobalStore";
import { TransactionDetail } from "types/history";
import { withIsVisible } from "utils/reactUtils";
import ArrowIcon from "../../../img/trade-history/PointedRight.svg?react";
import { NetworkCell } from "./NetworkCell";
import { TimestampCell } from "./TimestampCell";
import { getTokensBoughtOrSoldDescription } from "./TokensBoughtOrSold";
import { Hash } from "viem";
import { ActivityStore } from "../../../store/activity/ActivityStore";

interface TableRowMobileProps {
  rowDetails: Row<TransactionDetail>;
}

export const TableRowMobile = withIsVisible(({ rowDetails }: TableRowMobileProps) => {
  const theme = GlobalStore.useState(s => s.theme);
  const isTradeHistoryReturned = useIsTradeHistoryReturned();
  const timestamp = rowDetails.getValue<TransactionDetail["timestamp"]>("timestamp");
  const network = rowDetails.getValue<TransactionDetail["chain_id"]>("network");
  const sellTokenInfos = rowDetails.getValue<TransactionDetail["sellTokens"]>("sellTokens");
  const buyTokenInfos = rowDetails.getValue<TransactionDetail["buyTokens"]>("buyTokens");
  const volumeUsd = rowDetails.getValue<TransactionDetail["volumeUsd"]>("volumeUsd");
  const historyMetadata = ActivityStore.useState(s => s.historyMetadata);
  const sellTokens = Object.keys(sellTokenInfos) as Array<Hash>;
  const buyTokens = Object.keys(buyTokenInfos) as Array<Hash>;
  const sellTokensDescription = getTokensBoughtOrSoldDescription(
    sellTokens,
    network,
    historyMetadata,
    sellTokenInfos[sellTokens[0]]?.amount,
    true
  );
  const buyTokensDescription = getTokensBoughtOrSoldDescription(
    buyTokens,
    network,
    historyMetadata,
    buyTokenInfos[buyTokens[0]]?.amount,
    true
  );
  return (
    <Container>
      <Subcontainer>
        {isTradeHistoryReturned ? (
          <TimestampCell timestamp={timestamp} />
        ) : (
          <SkeletonBlock width={108} height={12} />
        )}
        <NetworkCell chainId={network} />
      </Subcontainer>
      <Subcontainer>
        <TokensContainer>
          {isTradeHistoryReturned ? (
            <TokenStackedArray
              chainId={network}
              tokenHistory={sellTokenInfos}
              direction={Direction.Horizontal}
              tokenSize={26}
            />
          ) : (
            <SkeletonCircle width={26} height={26} />
          )}
          <ArrowIcon color={isTradeHistoryReturned ? "inherit" : theme.theme[600]} />
          {isTradeHistoryReturned ? (
            <TokenStackedArray
              chainId={network}
              tokenHistory={buyTokenInfos}
              direction={Direction.Horizontal}
              tokenSize={26}
            />
          ) : (
            <SkeletonCircle width={26} height={26} />
          )}
        </TokensContainer>
        {isTradeHistoryReturned ? (
          <VolumeText>${volumeUsd.format(2)}</VolumeText>
        ) : (
          <SkeletonBlock width={92} height={16} />
        )}
      </Subcontainer>
      {isTradeHistoryReturned ? (
        <DescriptionContainer>
          <Description>{sellTokensDescription}</Description>
          <ArrowIcon width={12} height={12} />
          <Description>{buyTokensDescription}</Description>
        </DescriptionContainer>
      ) : (
        <SkeletonBlock width={195} height={12} />
      )}
    </Container>
  );
});

// Sub-components

const Container = styled.div`
  display: flex;
  padding: 12px;
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;
  align-self: stretch;
  margin-right: 0px;

  &:hover {
    border-radius: 17px;
    background: ${props => props.theme.surface.main.secondary.default};
  }
`;

const Subcontainer = styled.div`
  width: 100%;
  ${flexBetween}
`;

const VolumeText = styled.span`
  text-align: right;
  font-size: ${props => props.theme.fontSize[6]};
  line-height: ${props => props.theme.lineHeights[6]};
`;

const TokensContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const DescriptionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Description = styled.span`
  font-size: ${props => props.theme.fontSize[4]};
  line-height: ${props => props.theme.lineHeights[6]};
  max-width: 200px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;
